import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  Table,
  FormGroup,
  Label,
  Input,
  Button,
  Badge,
  Spinner,
  Alert,
} from "reactstrap";
import Swal from "sweetalert2";
import {
  checkoutReservation,
  createBilling,
  updatecheckouttimeAPI,
  markFoodBillingsAsPaidAPI,
  updateSpaBilling,
  BASE_URL,
  getToken,
} from "../../helpers/api.ts";
import HotelInvoice from "../Hotels/HotelBill.js";
import moment from "moment";

const CheckoutModal = ({
  isOpen,
  toggle,
  checkoutDetails,
  setCheckoutDetails,
  onCheckoutSuccess,
}) => {
  // ── Safe number helpers ───────────────────────────────────────────────────
  const n = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
  const r2 = (v) => Number(n(v).toFixed(2));
  const money = (v) => n(v).toFixed(2);

  // ── Time helpers ──────────────────────────────────────────────────────────
  const convertTo12Hour = (time24) => {
    if (!time24) return "";
    const [hours, minutes] = time24.split(":");
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? "PM" : "AM";
    const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${hour12.toString().padStart(2, "0")}:${minutes} ${ampm}`;
  };

  const convertTo24Hour = (time12) => {
    if (!time12) return "";
    const [time, ampm] = time12.split(" ");
    const [hours, minutes] = time.split(":");
    let hour = parseInt(hours, 10);
    if (ampm === "PM" && hour !== 12) hour += 12;
    else if (ampm === "AM" && hour === 12) hour = 0;
    return `${hour.toString().padStart(2, "0")}:${minutes}`;
  };

  const getCurrentTime12Hour = () => {
    const now = new Date();
    const hours = now.getHours();
    const minutes = now.getMinutes();
    const ampm = hours >= 12 ? "PM" : "AM";
    const hour12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    return `${hour12.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")} ${ampm}`;
  };

  // ── State ─────────────────────────────────────────────────────────────────
  const [roomGst, setRoomGst] = useState(0);
  const [foodGst, setFoodGst] = useState(0);
  const [spaGst, setSpaGst] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [resId, setResId] = useState(
    checkoutDetails?.reservationId || checkoutDetails?.reservation_id
  );
  const [checkoutTime, setCheckoutTime] = useState(getCurrentTime12Hour());

 const [foodFetchState, setFoodFetchState] = useState({
  loading: false,
  loaded: false,
  error: null,
  itemCount: 0,
  calculationMethod: null,
  hasPartialPayment: false,
  partialPaymentAmount: 0,
  fetchedAmount: 0,
  preSettled: false,

  // ✅ Restaurant display-only metadata
  foodPaymentStatus: "UNPAID",
  foodPaymentSource: "NONE",
  directFoodPaymentStatus: null,
  directFoodInvoiceNumbers: [],
});

  const [isFoodManuallyEdited, setIsFoodManuallyEdited] = useState(false);
  const [discountType, setDiscountType] = useState("INR"); // "INR" or "%"
  const [discountInput, setDiscountInput] = useState("");
  const modalOpenCountRef = useRef(0);

  // ── UI helpers ────────────────────────────────────────────────────────────
  const handlePrint = () => setShowModal((prev) => !prev);

  const isAllServicesCompleted = () =>
    checkoutDetails?.room_status === "COMPLETED" &&
    (checkoutDetails?.food_status === "COMPLETED" ||
      checkoutDetails?.food_status === "NOT_AVAILED") &&
    (checkoutDetails?.spa_status === "COMPLETED" ||
      checkoutDetails?.spa_status === "NOT_AVAILED") &&
    (checkoutDetails?.payment_mode ?? "") !== "" &&
    ["PAID", "PARTIAL"].includes(checkoutDetails?.payment_status ?? "");

  const currentFoodCharges = n(checkoutDetails?.food_charges);

  // ── Core billing calculator: discount first, GST after discount ──────────
  const buildCheckoutTotals = useCallback(
    (details) => {
     const rawRoomBase = r2(details?.room_charges);
const rawFoodBase = r2(details?.food_charges);
const rawSpaBase = r2(details?.spa_charges);
const addOnBase = r2(details?.add_on_charges);
const extraBedBase = r2(details?.extra_bed_charges);

const roomBase =
  details?.room_status === "NOT_AVAILED" ? 0 : rawRoomBase;

const foodBase =
  details?.food_status === "NOT_AVAILED" ? 0 : rawFoodBase;

const spaBase =
  details?.spa_status === "NOT_AVAILED" ? 0 : rawSpaBase;

      const lines = [
        {
          key: "room",
          label: "Room Charges",
          base: roomBase,
          gstRate: n(roomGst) / 100,
          discountable: true,
        },
        {
          key: "food",
          label: "Food & Beverages",
          base: foodBase,
          gstRate: n(foodGst) / 100,
          discountable: false,
        },
        {
          key: "spa",
          label: "Spa Services",
          base: spaBase,
          gstRate: n(spaGst) / 100,
          discountable: false,
        },
        {
          key: "addOn",
          label: "Add-on Charges",
          base: addOnBase,
          gstRate: 0,
          discountable: false,
        },
        {
          key: "extraBed",
          label: "Extra Bed Charges",
          base: extraBedBase,
          gstRate: 0,
          discountable: false,
        },
      ];

      const eligible = lines.filter((x) => x.discountable && x.base > 0);
      const eligibleSubtotal = r2(eligible.reduce((sum, x) => sum + x.base, 0));
      const subtotalBeforeDiscount = r2(
        lines.reduce((sum, x) => sum + x.base, 0)
      );

      const requestedDiscount = r2(details?.discount);
      const appliedDiscount = r2(Math.min(requestedDiscount, eligibleSubtotal));

      let usedDiscount = 0;
      const lastEligibleKey =
        eligible.length > 0 ? eligible[eligible.length - 1].key : null;

      const enriched = lines.map((line) => {
        let lineDiscount = 0;

        if (line.discountable && eligibleSubtotal > 0 && appliedDiscount > 0) {
          if (line.key === lastEligibleKey) {
            lineDiscount = r2(appliedDiscount - usedDiscount);
          } else {
            lineDiscount = r2((line.base / eligibleSubtotal) * appliedDiscount);
            usedDiscount = r2(usedDiscount + lineDiscount);
          }
        }

        const net = r2(line.base - lineDiscount);
        const gstAmount = r2(net * line.gstRate);
        const total = r2(net + gstAmount);

        return {
          ...line,
          discount: lineDiscount,
          net,
          gstAmount,
          total,
          gstRatePercent: r2(line.gstRate * 100),
        };
      });

      const totalGst = r2(enriched.reduce((sum, x) => sum + x.gstAmount, 0));
      const grandTotal = r2(enriched.reduce((sum, x) => sum + x.total, 0));

      const advance = r2(details?.Amount_Advance);
      const pendingRequested = r2(details?.pending_amount);
      const pendingAmount =
        details?.payment_status === "PARTIAL"
          ? r2(Math.min(pendingRequested, Math.max(grandTotal - advance, 0)))
          : 0;

      const amountToPayNow = r2(
        Math.max(grandTotal - advance - pendingAmount, 0)
      );

      const byKey = Object.fromEntries(enriched.map((x) => [x.key, x]));

      return {
        lines: enriched,
        byKey,
        subtotalBeforeDiscount,
        eligibleSubtotal,
        appliedDiscount,
        totalGst,
        grandTotal,
        pendingAmount,
        amountToPayNow,
      };
    },
    [roomGst, foodGst, spaGst]
  );

  const computedTotals = useMemo(
    () => buildCheckoutTotals(checkoutDetails ?? {}),
    [checkoutDetails, buildCheckoutTotals]
  );

  const paymentDetails = useMemo(
    () => ({
      subTotal: computedTotals.subtotalBeforeDiscount,
      totalGst: computedTotals.totalGst,
      grandTotal: computedTotals.grandTotal,
    }),
    [computedTotals]
  );

  const computedFinalAmount = computedTotals.amountToPayNow;
  const billLines = computedTotals.lines || [];
  const netAfterDiscount = r2(
    n(paymentDetails.subTotal) - n(computedTotals.appliedDiscount)
  );

  const roomLine = computedTotals.byKey?.room;
  const foodLine = computedTotals.byKey?.food;
  const spaLine = computedTotals.byKey?.spa;

  // ── Generic updater ───────────────────────────────────────────────────────
  const handleServiceUpdate = (serviceType, value, status) => {
  setCheckoutDetails((prev) => {
    const amountKey =
      serviceType === "extra_bed"
        ? "extra_bed_charges"
        : `${serviceType}_charges`;

    const nextStatus =
      serviceType !== "add_on" && serviceType !== "extra_bed" && status !== null
        ? status
        : prev[`${serviceType}_status`];

    const shouldZeroAmount =
      nextStatus === "NOT_AVAILED" &&
      (serviceType === "room" || serviceType === "food" || serviceType === "spa");

    const newDetails = {
      ...prev,
      [amountKey]: shouldZeroAmount ? 0 : n(value),
    };

    if (
      serviceType !== "add_on" &&
      serviceType !== "extra_bed" &&
      status !== null
    ) {
      newDetails[`${serviceType}_status`] = status;
    }

    const totals = buildCheckoutTotals(newDetails);

    return {
      ...newDetails,
      discount: totals.appliedDiscount,
      pending_amount: totals.pendingAmount,
      total_amount: totals.grandTotal,
      final_amount: totals.amountToPayNow,
    };
  });
};

  // ── Fetch food charges from API ───────────────────────────────────────────
  const fetchActualFoodCharges = useCallback(
    async (forceOverride = false) => {
      if (isFoodManuallyEdited && !forceOverride) {
        return n(checkoutDetails?.food_charges);
      }

      const customerId =
        checkoutDetails?.customer_id ||
        checkoutDetails?.guest_id ||
        checkoutDetails?.id ||
        0;

      const reservationId =
        checkoutDetails?.reservation_id ||
        checkoutDetails?.reservationId ||
        null;

      if (!customerId && !reservationId) {
        setFoodFetchState((prev) => ({
          ...prev,
          loaded: true,
          loading: false,
          error: "No customer or reservation ID available",
        }));
        return 0;
      }

      setFoodFetchState((prev) => ({
        ...prev,
        loading: true,
        error: null,
      }));

      try {
        const timestamp = Date.now();
        const queryParams = reservationId
          ? `?reservation_id=${reservationId}&_t=${timestamp}`
          : `?_t=${timestamp}`;

        const response = await fetch(
          `${BASE_URL}/Customermenu/checkout-food-charges/${customerId}${queryParams}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${getToken()}`,
              "Cache-Control": "no-cache, no-store, must-revalidate",
              Pragma: "no-cache",
              Expires: "0",
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch checkout food charges");
        }

        const data = await response.json();

        if (data?.success) {
  const fetchedAmount = n(data.totalCharges ?? data.total_food_amount);

  const foodPaymentStatus =
    data.food_payment_status ||
    data.direct_food_payment?.status ||
    (data.preSettled ? "PAID" : fetchedAmount > 0 ? "PENDING" : "UNPAID");

  const foodPaymentSource =
    data.food_payment_source ||
    (data.preSettled ? "DIRECT" : fetchedAmount > 0 ? "ROOM" : "NONE");

  const directFoodInvoiceNumbers =
    data.food_direct_invoice_numbers ||
    data.direct_food_payment?.invoiceNumbers ||
    [];

  setFoodFetchState({
    loading: false,
    loaded: true,
    error: null,
    itemCount: data.itemCount || 0,
    calculationMethod: data.calculationMethod || "unknown",
    hasPartialPayment: data.hasPartialPayment || false,
    partialPaymentAmount: data.partialPaymentAmount || 0,
    fetchedAmount,
    preSettled: !!data.preSettled,

    foodPaymentStatus,
    foodPaymentSource,
    directFoodPaymentStatus: data.direct_food_payment?.status || null,
    directFoodInvoiceNumbers,
  });

  if (!isFoodManuallyEdited || forceOverride) {
    setCheckoutDetails((prev) => {
      const newDetails = {
        ...prev,

        // ✅ Amount remains controlled by backend.
        // Direct-paid restaurant amount should come as 0 from API.
        food_charges: fetchedAmount,

        // ✅ Display-only restaurant payment metadata
        food_payment_status: foodPaymentStatus,
        food_payment_source: foodPaymentSource,
        direct_food_payment_status: data.direct_food_payment?.status || null,
        food_direct_invoice_numbers: directFoodInvoiceNumbers,
        direct_food_invoice_numbers: directFoodInvoiceNumbers,
      };

      const totals = buildCheckoutTotals(newDetails);

      return {
        ...newDetails,
        discount: totals.appliedDiscount,
        pending_amount: totals.pendingAmount,
        total_amount: totals.grandTotal,
        final_amount: totals.amountToPayNow,
      };
    });

    if (forceOverride) setIsFoodManuallyEdited(false);
  }

  return fetchedAmount;
}

        setFoodFetchState((prev) => ({
          ...prev,
          loading: false,
          loaded: true,
          error: data?.message || "No billing data found",
          fetchedAmount: 0,
        }));
        return 0;
      } catch (err) {
        setFoodFetchState((prev) => ({
          ...prev,
          loading: false,
          loaded: true,
          error: err?.message || "Failed to load food charges",
          fetchedAmount: 0,
        }));
        return 0;
      }
    },
    [
      checkoutDetails?.customer_id,
      checkoutDetails?.guest_id,
      checkoutDetails?.id,
      checkoutDetails?.reservation_id,
      checkoutDetails?.reservationId,
      checkoutDetails?.food_charges,
      isFoodManuallyEdited,
      buildCheckoutTotals,
    ]
  );

  // ── Fetch hotel GST ───────────────────────────────────────────────────────
  useEffect(() => {
    const fetchHotelGst = async () => {
      try {
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        const hotel_id =
          user?.role === "HOTEL_ADMIN"
            ? user?.hotel_id
            : user?.hotel?.hotel_id;

        if (!hotel_id) return;

        const response = await fetch(`${BASE_URL}/hotel/getHotelDetails`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getToken()}`,
          },
          body: JSON.stringify({ hotel_id }),
        });

        const data = await response.json();
        if (data?.success && data?.data) {
          const defaultGst = parseFloat(data.data.gst_percentage) || 0;
          setRoomGst(defaultGst);
          // The restaurant amount from the server (checkout-food-charges) is the sum of the
          // food bills' final_amount, which already includes each dish's GST. Applying the
          // hotel GST % on top charged GST on GST, so food starts at 0% here.
          setFoodGst(0);
          setSpaGst(defaultGst);
        }
      } catch (err) {
        console.warn("Could not fetch hotel GST:", err);
      }
    };

    fetchHotelGst();
  }, []);


  const getCheckoutNights = (details) => {
  const directNights = n(details?.number_of_nights);

  if (directNights > 0) {
    return directNights;
  }

  if (details?.check_in_date && details?.check_out_date) {
    const checkIn = moment(details.check_in_date).startOf("day");
    const checkOut = moment(details.check_out_date).startOf("day");
    const diff = checkOut.diff(checkIn, "days");

    return diff > 0 ? diff : 1;
  }

  return 1;
};

const getRoomRatePerNight = (details) => {
  return n(
    details?.room_rate_per_night ??
      details?.room_details?.tariff ??
      details?.tariff_charges
  );
};


  // ── Init on modal open ────────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;

    modalOpenCountRef.current += 1;
    const openCount = modalOpenCountRef.current;

    setIsFoodManuallyEdited(false);

    const totalSpaCharges = n(
      checkoutDetails?.total_spa_charges ?? checkoutDetails?.spa_charges ?? 0
    );

    const initializeCharges = async () => {
      const actualFoodCharges = await fetchActualFoodCharges(false);
      if (modalOpenCountRef.current !== openCount) return;

      setCheckoutDetails((prev) => {


       const nights = getCheckoutNights(prev);
const perNightRate = getRoomRatePerNight(prev);

const roomCharges =
  perNightRate > 0
    ? r2(perNightRate * nights)
    : r2(prev?.room_charges);

        const newDetails = {
          ...prev,
          room_charges: roomCharges,
          food_charges: actualFoodCharges,
          spa_charges: totalSpaCharges,
          total_spa_charges: totalSpaCharges,
        };

        const totals = buildCheckoutTotals(newDetails);

        return {
          ...newDetails,
          discount: totals.appliedDiscount,
          pending_amount: totals.pendingAmount,
          total_amount: totals.grandTotal,
          final_amount: totals.amountToPayNow,
        };
      });
    };

    initializeCharges();

    if (checkoutDetails?.billing_details) {
      const bd = checkoutDetails.billing_details;
      if (bd.room_gst) setRoomGst(n(bd.room_gst));
      if (bd.food_gst) setFoodGst(n(bd.food_gst));
      if (bd.spa_gst) setSpaGst(n(bd.spa_gst));
    }

    setResId(checkoutDetails?.reservationId || checkoutDetails?.reservation_id);
    setCheckoutTime(getCurrentTime12Hour());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // ── Input handlers ────────────────────────────────────────────────────────
  const handleFoodChargesChange = (e) => {
    const val = Math.round(n(e.target.value));
    setIsFoodManuallyEdited(true);
    handleServiceUpdate("food", val, checkoutDetails?.food_status);
  };

  const handleExtraBedChargesChange = (value) => {
    handleServiceUpdate("extra_bed", value, null);
  };

  const handleDiscountChange = (value, type) => {
    const resolvedType = type ?? discountType;
    setCheckoutDetails((prev) => {
      const preview = buildCheckoutTotals({ ...prev, discount: 0 });
      const roomBase = preview.eligibleSubtotal; // only room is discountable now
      let inrDiscount;
      if (resolvedType === "%") {
        const pct = Math.min(n(value), 100);
        const clampedPct = String(pct);
        setDiscountInput(clampedPct);
        inrDiscount = r2((roomBase * pct) / 100);
      } else {
        const clampedInr = Math.min(n(value), roomBase);
        setDiscountInput(String(clampedInr));
        inrDiscount = r2(clampedInr);
      }

      const newDetails = { ...prev, discount: inrDiscount };
      const totals = buildCheckoutTotals(newDetails);

      return {
        ...newDetails,
        pending_amount: totals.pendingAmount,
        total_amount: totals.grandTotal,
        final_amount: totals.amountToPayNow,
      };
    });
  };

  const handleDiscountTypeChange = (newType) => {
    setDiscountType(newType);
    setDiscountInput("");
    handleDiscountChange("0", newType);
  };

  const handlePaymentModeChange = (mode) =>
    setCheckoutDetails((prev) => ({ ...prev, payment_mode: mode }));

  const handlePaymentStatusChange = (status) => {
    setCheckoutDetails((prev) => {
      const newDetails = {
        ...prev,
        payment_status: status,
        pending_amount: status === "PARTIAL" ? n(prev.pending_amount) : 0,
      };

      const totals = buildCheckoutTotals(newDetails);

      return {
        ...newDetails,
        pending_amount: totals.pendingAmount,
        total_amount: totals.grandTotal,
        final_amount: totals.amountToPayNow,
      };
    });
  };

  const handleNotesChange = (notes) =>
    setCheckoutDetails((prev) => ({ ...prev, notes }));

  const handlePendingAmountChange = (amount) => {
    setCheckoutDetails((prev) => {
      const preview = buildCheckoutTotals({
        ...prev,
        payment_status: "PARTIAL",
        pending_amount: 0,
      });

      const maxPending = r2(
        Math.max(preview.grandTotal - n(prev.Amount_Advance), 0)
      );
      const safePending = r2(Math.min(n(amount), maxPending));

      const newDetails = {
        ...prev,
        payment_status: "PARTIAL",
        pending_amount: safePending,
      };

      const totals = buildCheckoutTotals(newDetails);

      return {
        ...newDetails,
        pending_amount: totals.pendingAmount,
        total_amount: totals.grandTotal,
        final_amount: totals.amountToPayNow,
      };
    });
  };


  const ServicePaymentStatusBadge = ({ label, status, source }) => {
  const normalizedStatus = status ? String(status).toUpperCase() : "UNPAID";
  const normalizedSource = source ? String(source).toUpperCase() : "NONE";

  let color = "secondary";
  let text = "No Bill";

  if (normalizedSource === "DIRECT" || normalizedSource === "DIRECT_RESTAURANT") {
    color = normalizedStatus === "PAID" ? "success" : "warning";
    text = normalizedStatus === "PAID" ? "Direct Paid" : `Direct ${normalizedStatus}`;
  } else if (normalizedSource === "ROOM" || normalizedSource === "CHARGE_TO_ROOM") {
    color = normalizedStatus === "PARTIALLY_PAID" ? "warning" : "info";
    text =
      normalizedStatus === "PARTIALLY_PAID"
        ? "Charge to Room - Partial"
        : "Charge to Room";
  } else if (normalizedStatus === "PAID") {
    color = "success";
    text = "Paid";
  } else if (normalizedStatus === "PENDING") {
    color = "warning";
    text = "Pending";
  }

  return (
    <Badge color={color} style={{ fontSize: "0.65rem" }}>
      {label}: {text}
    </Badge>
  );
};

  // ── Food badge ────────────────────────────────────────────────────────────
  const getFoodChargesBadge = () => {
    if (foodFetchState.loading) return null;

    if (isFoodManuallyEdited) {
      return (
        <Badge color="warning" style={{ fontSize: "0.65rem" }}>
          Manually edited
        </Badge>
      );
    }

    if (!foodFetchState.loaded) return null;

    if (foodFetchState.itemCount === 0) {
      return (
        <Badge color="secondary" style={{ fontSize: "0.65rem" }}>
          No items
        </Badge>
      );
    }

    if (foodFetchState.preSettled) {
      return (
        <Badge color="success" style={{ fontSize: "0.65rem" }}>
          Pre-settled at restaurant
        </Badge>
      );
    }

    if (foodFetchState.hasPartialPayment) {
      return (
        <Badge color="warning" style={{ fontSize: "0.65rem" }}>
          Partial billed: ₹{money(foodFetchState.partialPaymentAmount)}
        </Badge>
      );
    }

    return (
      <Badge color="info" style={{ fontSize: "0.65rem" }}>
        {foodFetchState.calculationMethod?.includes("RoomAllocated")
          ? "Charge-to-Room"
          : "Auto-fetched"}
      </Badge>
    );
  };

  // ── Checkout ──────────────────────────────────────────────────────────────
  const handleCheckout = async () => {
    try {
      const currentDate = new Date().toISOString().split("T")[0];
      const scheduledCheckoutDate = checkoutDetails?.check_out_date;

      if (currentDate < scheduledCheckoutDate) {
        await updatecheckouttimeAPI({
          reservation_id: resId,
          check_out_time: checkoutTime,
          check_out_date: currentDate,
        });
      } else {
        await updatecheckouttimeAPI({
          reservation_id: resId,
          check_out_time: checkoutTime,
        });
      }

      const totals = buildCheckoutTotals(checkoutDetails ?? {});
      const finalFoodCharges = totals.byKey.food.base;
      const isPartialPayment = checkoutDetails?.payment_status === "PARTIAL";

      const billingData = {
        reservation_id: resId,

        room_charges: totals.byKey.room.base,
        food_charges: totals.byKey.food.base,
        spa_charges: totals.byKey.spa.base,
        add_on_charges: totals.byKey.addOn.base,
        extra_bed_charges: totals.byKey.extraBed.base,

        discount: totals.appliedDiscount,
        Amount_Advance: n(checkoutDetails?.Amount_Advance),
        payment_mode: checkoutDetails?.payment_mode,
        additional_note: checkoutDetails?.notes || "",

        room_gst: n(roomGst),
        food_gst: n(foodGst),
        spa_gst: n(spaGst),

        total_room_amount: totals.byKey.room.total,
        total_food_amount: totals.byKey.food.total,
        total_spa_amount: totals.byKey.spa.total,

        total_gst_amount: totals.totalGst,
        subtotal: totals.subtotalBeforeDiscount,
        final_amount: totals.amountToPayNow,
        pending_amount: isPartialPayment ? totals.pendingAmount : 0,

        // ✅ Restaurant display metadata
food_payment_status: checkoutDetails?.food_payment_status || "UNPAID",
food_payment_source: checkoutDetails?.food_payment_source || "NONE",
food_direct_invoice_numbers:
  checkoutDetails?.food_direct_invoice_numbers?.join(",") ||
  checkoutDetails?.direct_food_invoice_numbers?.join(",") ||
  null,

// ✅ SPA display metadata
spa_payment_status: checkoutDetails?.spa_payment_status || "UNPAID",
spa_payment_source: checkoutDetails?.spa_payment_source || "NONE",
spa_direct_invoice_numbers:
  checkoutDetails?.spa_direct_invoice_numbers?.join(",") || null,
      };

      await createBilling(billingData);

      try {
        await markFoodBillingsAsPaidAPI({
          reservation_id: resId,
        });
      } catch (foodBillingError) {
        console.error("Error marking food billings as paid:", foodBillingError);
      }

      const response = await checkoutReservation(resId, {
        ...checkoutDetails,
        food_charges: finalFoodCharges,
        subtotal: totals.subtotalBeforeDiscount,
        total_gst_amount: totals.totalGst,
        final_amount: totals.amountToPayNow,
        pending_amount: totals.pendingAmount,
        additional_Note: checkoutDetails?.notes || "",
      });

      if (response?.success) {
        // The server's checkout does not touch spa billing, so spa bills charged to the
        // room stayed "Unpaid" on the Spa page after the final bill was paid.
        try {
          const spaBills = Array.isArray(checkoutDetails?.spa_billing_details)
            ? checkoutDetails.spa_billing_details
            : [];
          if (checkoutDetails?.spa_status !== "NOT_AVAILED") {
            await Promise.all(
              spaBills
                .filter(
                  (b) =>
                    b &&
                    (b.id ?? b.spa_billing_id) != null &&
                    String(b.billing_type || "").toUpperCase() === "ROOM" &&
                    String(b.payment_status || "").toUpperCase() !== "PAID"
                )
                .map((b) => updateSpaBilling(b.id ?? b.spa_billing_id, { payment_status: "PAID" }))
            );
          }
        } catch (spaPaidError) {
          console.error("Could not mark spa bills as paid:", spaPaidError);
        }

        if (onCheckoutSuccess) onCheckoutSuccess(resId);

        Swal.fire({
          icon: "success",
          title: "Checkout Successful",
          html: `
            <p><strong>Guest:</strong> ${checkoutDetails?.guest_name ?? ""}</p>
            <p><strong>Room:</strong> ${checkoutDetails?.room_number ?? ""}</p>
            <p><strong>Check-out Time:</strong> ${checkoutTime}</p>
            <hr/>
            <p><strong>Base Amount:</strong> ₹${money(paymentDetails.subTotal)}</p>
            <p><strong>Discount:</strong> - ₹${money(totals.appliedDiscount)}</p>
            <p><strong>Net Amount:</strong> ₹${money(netAfterDiscount)}</p>
            <p><strong>Total GST:</strong> ₹${money(paymentDetails.totalGst)}</p>
            <p><strong>Bill Total:</strong> ₹${money(paymentDetails.grandTotal)}</p>
            ${
              n(checkoutDetails?.Amount_Advance) > 0
                ? `<p><strong>Advance Paid:</strong> - ₹${money(
                    checkoutDetails.Amount_Advance
                  )}</p>`
                : ""
            }
            ${
  checkoutDetails?.food_payment_status
    ? `<p><strong>Restaurant Payment:</strong> ${
        checkoutDetails.food_payment_source === "DIRECT" ||
        checkoutDetails.food_payment_source === "DIRECT_RESTAURANT"
          ? "Direct Paid"
          : checkoutDetails.food_payment_source === "ROOM" ||
            checkoutDetails.food_payment_source === "CHARGE_TO_ROOM"
            ? "Charge to Room"
            : checkoutDetails.food_payment_status
      }</p>`
    : ""
}

${
  checkoutDetails?.spa_payment_status
    ? `<p><strong>SPA Payment:</strong> ${
        checkoutDetails.spa_payment_source === "DIRECT"
          ? "Direct Paid"
          : checkoutDetails.spa_payment_source === "ROOM"
            ? "Charge to Room"
            : checkoutDetails.spa_payment_status
      }</p>`
    : ""
}
            <p><strong>Amount to Pay:</strong> <span class="text-primary">₹${money(
              totals.amountToPayNow
            )}</span></p>
            ${
              totals.pendingAmount > 0
                ? `<p><strong>Pending:</strong> <span class="text-warning">₹${money(
                    totals.pendingAmount
                  )}</span></p>`
                : ""
            }
          `,
        });

        toggle();
      }
    } catch (error) {
      console.error("Error completing checkout:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to complete checkout.",
      });
    }
  };

  return (
    <>
      <Modal isOpen={isOpen} toggle={toggle} size="lg">
        <ModalHeader toggle={toggle}>
          <div className="d-flex justify-content-between align-items-center w-100">
            <h4 className="mb-0">Checkout Details</h4>
            {foodFetchState.loading && (
              <Spinner size="sm" color="primary" className="ms-2" />
            )}
          </div>
        </ModalHeader>

        <ModalBody>
          <div className="p-4">
            <div className="mb-4">
              <h5 className="border-bottom pb-2">Guest Information</h5>
              <div className="row">
                <div className="col-md-6">
                  <p>
                    <strong>Room:</strong> {checkoutDetails?.room_number ?? ""}
                  </p>
                  <p>
                    <strong>Guest:</strong> {checkoutDetails?.guest_name ?? ""}
                  </p>
                </div>
                <div className="col-md-6">
                  <p>
                    <strong>Check In:</strong>{" "}
                    {checkoutDetails?.check_in_date
                      ? moment(checkoutDetails.check_in_date).format("DD-MM-YYYY")
                      : "—"}
                  </p>
                  <p>
                    <strong>Check Out:</strong>{" "}
                    {checkoutDetails?.check_out_date
                      ? moment(checkoutDetails.check_out_date).format("DD-MM-YYYY")
                      : "—"}
                  </p>
                </div>
              </div>

              {foodFetchState.error && (
                <Alert color="warning" className="mb-3 py-2">
                  <small>
                    ⚠️ Food charges: {foodFetchState.error}. You can enter the
                    amount manually below.
                  </small>
                </Alert>
              )}

              <div className="mb-4 p-3 bg-light rounded">
                <h6 className="mb-3">Checkout Time</h6>
                {(() => {
                  const currentDate = new Date().toISOString().split("T")[0];
                  const isEarly = currentDate < checkoutDetails?.check_out_date;
                  return (
                    isEarly && (
                      <div className="alert alert-warning mb-3" role="alert">
                        <i className="fas fa-exclamation-triangle me-2"></i>
                        <strong>Early Checkout Detected!</strong>
                        <br />
                        <small>
                          Current date ({currentDate}) is before scheduled
                          checkout ({checkoutDetails?.check_out_date}). Date
                          will be updated to today.
                        </small>
                      </div>
                    )
                  );
                })()}
                <div className="row">
                  <div className="col-md-8">
                    <FormGroup>
                      <Label for="checkout_time">Check-out Time *</Label>
                      <div className="d-flex align-items-center gap-2">
                        <Input
                          type="time"
                          value={convertTo24Hour(checkoutTime)}
                          onChange={(e) =>
                            setCheckoutTime(convertTo12Hour(e.target.value))
                          }
                          required
                        />
                        <span className="fw-bold text-primary">{checkoutTime}</span>
                      </div>
                    </FormGroup>
                  </div>
                </div>
              </div>
            </div>

            <Table bordered responsive>
              <thead className="bg-light">
                <tr>
                  <th>Service</th>
                  <th>Base Amount (₹)</th>
                  <th>GST %</th>
                  <th>GST Amount (₹)</th>
                  <th>Total (₹)</th>
                  <th>Status</th>
                  <th>Current Status</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <strong>Room Charges</strong>
                  </td>
                  <td>
                    <Input
                      type="number"
                      value={n(checkoutDetails?.room_charges)}
                      onChange={(e) =>
                        handleServiceUpdate(
                          "room",
                          e.target.value,
                          checkoutDetails?.room_status
                        )
                      }
                      className="form-control-sm"
                    />
                  </td>
                  <td>
                    <Input
                      type="number"
                      value={n(roomGst)}
                      onChange={(e) => setRoomGst(n(e.target.value))}
                      className="form-control-sm"
                    />
                  </td>
                  <td>{money(roomLine?.gstAmount)}</td>
                  <td>{money(roomLine?.total)}</td>
                  <td>
                    <Input
                      type="select"
                      value={checkoutDetails?.room_status ?? "PENDING"}
                      onChange={(e) =>
                        handleServiceUpdate(
                          "room",
                          checkoutDetails?.room_charges,
                          e.target.value
                        )
                      }
                      className="form-control-sm"
                    >
                      <option value="PENDING">Pending</option>
                      <option value="COMPLETED">Completed</option>
                      <option value="NOT_AVAILED">Not Availed</option>
                    </Input>
                  </td>
                  <td>
                    <Badge
                      color={
                        checkoutDetails?.room_status === "COMPLETED"
                          ? "success"
                          : "warning"
                      }
                    >
                      {checkoutDetails?.room_status ?? "—"}
                    </Badge>
                  </td>
                </tr>

                <tr style={{ background: isFoodManuallyEdited ? "#fffbf0" : "inherit" }}>
                  <td>
                    <div className="d-flex flex-column gap-1">
                     <strong>Food &amp; Beverages</strong>

{getFoodChargesBadge()}

<ServicePaymentStatusBadge
  label="Restaurant"
  status={
    checkoutDetails?.food_payment_status ||
    foodFetchState.foodPaymentStatus
  }
  source={
    checkoutDetails?.food_payment_source ||
    foodFetchState.foodPaymentSource
  }
/>
                    </div>
                  </td>

                  <td>
                    <div>
                      <Input
                        type="number"
                        value={Math.round(currentFoodCharges)}
                        onChange={handleFoodChargesChange}
                        className="form-control-sm"
                        step="1"
                        min="0"
                        style={
                          isFoodManuallyEdited
                            ? { borderColor: "#f0a500", background: "#fffdf0" }
                            : {}
                        }
                      />
                      {foodFetchState.loading && (
                        <small className="text-muted d-block mt-1">
                          <Spinner size="sm" className="me-1" />
                          Loading…
                        </small>
                      )}
                      {isFoodManuallyEdited && (
                        <small className="text-warning d-block mt-1">
                          ✏️ Overridden
                        </small>
                      )}
                    </div>
                  </td>

                  <td>
                    <Input
                      type="number"
                      value={n(foodGst)}
                      onChange={(e) => setFoodGst(n(e.target.value))}
                      className="form-control-sm"
                    />
                  </td>
                  <td>{money(foodLine?.gstAmount)}</td>
                  <td>{money(foodLine?.total)}</td>
                  <td>
                    <Input
                      type="select"
                      value={checkoutDetails?.food_status ?? "PENDING"}
                      onChange={(e) =>
                        handleServiceUpdate("food", currentFoodCharges, e.target.value)
                      }
                      className="form-control-sm"
                    >
                      <option value="PENDING">Pending</option>
                      <option value="COMPLETED">Completed</option>
                      <option value="NOT_AVAILED">Not Availed</option>
                    </Input>
                  </td>
                  <td>
                    <Badge
                      color={
                        checkoutDetails?.food_status === "COMPLETED"
                          ? "success"
                          : "warning"
                      }
                    >
                      {checkoutDetails?.food_status ?? "—"}
                    </Badge>
                  </td>
                </tr>

                <tr>
                  <td>
                    <strong>Spa Services</strong>

<div className="mt-1">
  <ServicePaymentStatusBadge
    label="SPA"
    status={checkoutDetails?.spa_payment_status}
    source={checkoutDetails?.spa_payment_source}
  />
</div>
                  </td>
                  <td>
                    <Input
                      type="number"
                      value={n(checkoutDetails?.spa_charges)}
                      onChange={(e) =>
                        handleServiceUpdate(
                          "spa",
                          e.target.value,
                          checkoutDetails?.spa_status
                        )
                      }
                      className="form-control-sm"
                    />
                  </td>
                  <td>
                    <Input
                      type="number"
                      value={n(spaGst)}
                      onChange={(e) => setSpaGst(n(e.target.value))}
                      className="form-control-sm"
                    />
                  </td>
                  <td>{money(spaLine?.gstAmount)}</td>
                  <td>{money(spaLine?.total)}</td>
                  <td>
                    <Input
                      type="select"
                      value={checkoutDetails?.spa_status ?? "PENDING"}
                      onChange={(e) =>
                        handleServiceUpdate(
                          "spa",
                          checkoutDetails?.spa_charges,
                          e.target.value
                        )
                      }
                      className="form-control-sm"
                    >
                      <option value="PENDING">Pending</option>
                      <option value="COMPLETED">Completed</option>
                      <option value="NOT_AVAILED">Not Availed</option>
                    </Input>
                  </td>
                  <td>
                    <Badge
                      color={
                        checkoutDetails?.spa_status === "COMPLETED"
                          ? "success"
                          : "warning"
                      }
                    >
                      {checkoutDetails?.spa_status ?? "—"}
                    </Badge>
                  </td>
                </tr>

                <tr>
                  <td>
                    <strong>Add-on Charges</strong>
                  </td>
                  <td>
                    <Input
                      type="number"
                      value={
                        n(checkoutDetails?.add_on_charges) === 0
                          ? ""
                          : n(checkoutDetails?.add_on_charges)
                      }
                      onChange={(e) =>
                        handleServiceUpdate("add_on", e.target.value, null)
                      }
                      className="form-control-sm"
                      placeholder="Enter amount"
                    />
                  </td>
                  <td colSpan="4" className="text-center">
                    <strong>₹{money(checkoutDetails?.add_on_charges)}</strong>
                    <br />
                    <small className="text-muted">No GST Applied</small>
                  </td>
                </tr>

                <tr>
                  <td>
                    <strong>Extra Bed Charges</strong>
                    <br />
                    <small className="text-muted">Edit if extra bed added</small>
                  </td>
                  <td>
                    <Input
                      type="number"
                      value={
                        n(checkoutDetails?.extra_bed_charges) === 0
                          ? ""
                          : n(checkoutDetails?.extra_bed_charges)
                      }
                      onChange={(e) => handleExtraBedChargesChange(e.target.value)}
                      className="form-control-sm"
                      placeholder="0"
                      min="0"
                    />
                  </td>
                  <td colSpan="4" className="text-center">
                    <strong>₹{money(checkoutDetails?.extra_bed_charges)}</strong>
                    <br />
                    <small className="text-muted">No GST Applied</small>
                  </td>
                </tr>

                <tr className="table-info">
                  <td colSpan="4">
                    <strong>Base Amount</strong>
                  </td>
                  <td colSpan="3">
                    <strong>₹{money(paymentDetails.subTotal)}</strong>
                  </td>
                </tr>

                <tr>
                  <td colSpan="4">
                    <strong>Discount</strong>
                    <small className="text-muted d-block">On Room Charges only</small>
                  </td>
                  <td colSpan="3">
                    <div className="d-flex align-items-center gap-1">
                      <div className="btn-group btn-group-sm me-1" role="group">
                        <button
                          type="button"
                          className={`btn btn-sm ${discountType === "INR" ? "btn-primary" : "btn-outline-secondary"}`}
                          onClick={() => handleDiscountTypeChange("INR")}
                        >
                          ₹
                        </button>
                        <button
                          type="button"
                          className={`btn btn-sm ${discountType === "%" ? "btn-primary" : "btn-outline-secondary"}`}
                          onClick={() => handleDiscountTypeChange("%")}
                        >
                          %
                        </button>
                      </div>
                      <Input
                        type="number"
                        min="0"
                        max={discountType === "%" ? 100 : computedTotals.eligibleSubtotal}
                        value={discountInput}
                        placeholder={discountType === "%" ? "0–100" : "0"}
                        onChange={(e) => handleDiscountChange(e.target.value)}
                        className="form-control-sm"
                        style={{ width: "90px" }}
                      />
                      {discountType === "%" && n(checkoutDetails?.discount) > 0 && (
                        <small className="text-muted ms-1">
                          = ₹{money(checkoutDetails?.discount)}
                        </small>
                      )}
                    </div>
                  </td>
                </tr>

                <tr className="table-warning">
                  <td colSpan="4">
                    <strong>Net Amount After Discount</strong>
                  </td>
                  <td colSpan="3">
                    <strong>₹{money(netAfterDiscount)}</strong>
                  </td>
                </tr>

                <tr className="table-info">
                  <td colSpan="4">
                    <strong>Total GST</strong>
                  </td>
                  <td colSpan="3">
                    <strong>₹{money(paymentDetails.totalGst)}</strong>
                  </td>
                </tr>

                <tr className="table-primary">
                  <td colSpan="4">
                    <strong>Bill Total (Net + GST)</strong>
                  </td>
                  <td colSpan="3">
                    <strong>₹{money(paymentDetails.grandTotal)}</strong>
                  </td>
                </tr>

                <tr className="table-warning">
                  <td colSpan="4">
                    <strong>Advance Amount Paid</strong>
                    <br />
                    <small className="text-muted">(Paid during reservation)</small>
                  </td>
                  <td colSpan="3">
                    <strong className="text-success">
                      - ₹{money(checkoutDetails?.Amount_Advance)}
                    </strong>
                  </td>
                </tr>

                {n(checkoutDetails?.pending_amount) > 0 && (
                  <tr className="table-info">
                    <td colSpan="4">
                      <strong>Pending Amount</strong>
                    </td>
                    <td colSpan="3">
                      <strong className="text-warning">
                        - ₹{money(checkoutDetails?.pending_amount)}
                      </strong>
                    </td>
                  </tr>
                )}

                <tr className="table-success">
                  <td colSpan="4">
                    <strong>Final Amount to Pay Now</strong>
                  </td>
                  <td colSpan="3">
                    <strong>₹{money(computedFinalAmount)}</strong>
                  </td>
                </tr>
              </tbody>
            </Table>

            {n(computedTotals.appliedDiscount) > 0 && (
              <div className="mb-4">
                <h6 className="mb-2">
                  Discount Breakdown (Discount first, GST after discount)
                </h6>
                <Table bordered responsive size="sm">
                  <thead className="bg-light">
                    <tr>
                      <th>Service</th>
                      <th>Base</th>
                      <th>Discount Share</th>
                      <th>Net After Discount</th>
                      <th>GST on Net</th>
                      <th>Line Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {billLines.map((line) => (
                      <tr key={line.key}>
                        <td>{line.label}</td>
                        <td>₹{money(line.base)}</td>
                        <td style={{ color: "#e65100", fontWeight: 600 }}>
                          {line.discount > 0 ? `- ₹${money(line.discount)}` : "—"}
                        </td>
                        <td>₹{money(line.net)}</td>
                        <td style={{ color: "#1a7a3c", fontWeight: 600 }}>
                          {line.gstAmount > 0 ? `₹${money(line.gstAmount)}` : "—"}
                        </td>
                        <td style={{ fontWeight: 700 }}>₹{money(line.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}

            <div className="mt-4">
              <FormGroup>
                <Label>
                  Payment Mode <span className="text-danger">*</span>
                </Label>
                <Input
                  type="select"
                  value={checkoutDetails?.payment_mode ?? ""}
                  onChange={(e) => handlePaymentModeChange(e.target.value)}
                  invalid={!checkoutDetails?.payment_mode}
                >
                  <option value="">Select Payment Mode</option>
                  <option value="CASH">Cash</option>
                  <option value="CARD">Card</option>
                  <option value="UPI">UPI</option>
                  <option value="BANK_TRANSFER">Bank Transfer</option>
                </Input>
              </FormGroup>

              <FormGroup className="mt-3">
                <Label>
                  Payment Status <span className="text-danger">*</span>
                </Label>
                <Input
                  type="select"
                  value={checkoutDetails?.payment_status ?? ""}
                  onChange={(e) => handlePaymentStatusChange(e.target.value)}
                  invalid={!checkoutDetails?.payment_status}
                >
                  <option value="">Select Payment Status</option>
                  <option value="PAID">Paid</option>
                  <option value="PENDING">Pending</option>
                  <option value="PARTIAL">Partial</option>
                </Input>
              </FormGroup>

              <FormGroup className="mt-3">
                <Label>Additional Notes</Label>
                <Input
                  type="textarea"
                  value={checkoutDetails?.notes ?? ""}
                  onChange={(e) => handleNotesChange(e.target.value)}
                  placeholder="Enter any additional notes..."
                  rows="3"
                />
              </FormGroup>

              <FormGroup className="mt-3">
                <Label>Pending Amount (₹)</Label>
                <Input
                  type="number"
                  value={
                    checkoutDetails?.payment_status === "PARTIAL"
                      ? n(checkoutDetails?.pending_amount)
                      : 0
                  }
                  onChange={(e) => {
                    if (checkoutDetails?.payment_status === "PARTIAL") {
                      handlePendingAmountChange(e.target.value);
                    }
                  }}
                  min="0"
                  step="0.01"
                  disabled={checkoutDetails?.payment_status !== "PARTIAL"}
                />
                <small className="text-muted">
                  {checkoutDetails?.payment_status === "PARTIAL"
                    ? "Enter amount that will be paid later"
                    : "Select 'Partial' payment status to enter a pending amount"}
                </small>
              </FormGroup>
            </div>
          </div>
        </ModalBody>

        <div className="modal-footer">
          <Button color="info" onClick={handlePrint}>
            Print Bill
          </Button>
          <Button color="secondary" onClick={toggle}>
            Cancel
          </Button>

          {isAllServicesCompleted() ? (
            <Button
              color="primary"
              onClick={handleCheckout}
              disabled={foodFetchState.loading}
            >
              {foodFetchState.loading ? (
                <>
                  <Spinner size="sm" className="me-2" /> Loading...
                </>
              ) : (
                "Complete Checkout"
              )}
            </Button>
          ) : (
            <div className="text-danger small">
              * Complete all services before checkout
            </div>
          )}
        </div>
      </Modal>

      <Modal isOpen={showModal} toggle={handlePrint} size="lg">
        <HotelInvoice
  checkoutData={{
    id: checkoutDetails?.guest_id,

    charges: {
      roomCharges: n(checkoutDetails?.room_charges),
      foodCharges: currentFoodCharges,
      spaCharges: n(checkoutDetails?.spa_charges),
      extraBedCharges: n(checkoutDetails?.extra_bed_charges),
    },

    servicePayments: {
      food: {
        status: checkoutDetails?.food_payment_status || "UNPAID",
        source: checkoutDetails?.food_payment_source || "NONE",
        invoiceNumbers:
          checkoutDetails?.food_direct_invoice_numbers ||
          checkoutDetails?.direct_food_invoice_numbers || 
          [],
      },
      spa: {
        status: checkoutDetails?.spa_payment_status || "UNPAID",
        source: checkoutDetails?.spa_payment_source || "NONE",
        invoiceNumbers: checkoutDetails?.spa_direct_invoice_numbers || [],
      },
    },

    gstRates: { roomGst, foodGst, spaGst },

    calculatedTotals: {
      subTotal: n(paymentDetails.subTotal),
      totalGst: n(paymentDetails.totalGst),
      grandTotal: n(computedFinalAmount),
    },

    discount: n(checkoutDetails?.discount),
    paymentMode: checkoutDetails?.payment_mode,

    serviceStatus: {
      room: checkoutDetails?.room_status,
      food: checkoutDetails?.food_status,
      spa: checkoutDetails?.spa_status,
    },
  }}
/>
      </Modal>
    </>
  );
};

export default CheckoutModal;
